import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import { QueueFilters } from "@/components/queue/QueueFilters";
import { QueueJobDrawer } from "@/components/queue/QueueJobDrawer";
import { QueueKpis } from "@/components/queue/QueueKpis";
import { QueueTable } from "@/components/queue/QueueTable";
import type { QueueJobStatus, QueueJobType } from "@/lib/api";
import { usePolling } from "@/lib/usePolling";
import { useAuthStore } from "@/state/authStore";
import { useQueueStore } from "@/state/queueStore";
import { useShallow } from "zustand/react/shallow";

const POLL_INTERVAL_MS = 5000;

export function Queue() {
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    jobs,
    total,
    summary,
    selectedJobId,
    diagnosticsByJobId,
    filters,
    pagination,
    loading,
    diagnosticsLoading,
    summaryLoading,
    error,
    autoRefresh,
    fetchJobs,
    fetchSummary,
    fetchJobDiagnostics,
    setFilters,
    resetFilters,
    setSelectedJobId,
    setAutoRefresh,
    nextPage,
    prevPage,
    retryJob,
    cancelJob,
    updateJobPriority,
  } = useQueueStore(
    useShallow((state) => ({
      jobs: state.jobs,
      total: state.total,
      summary: state.summary,
      selectedJobId: state.selectedJobId,
      diagnosticsByJobId: state.diagnosticsByJobId,
      filters: state.filters,
      pagination: state.pagination,
      loading: state.loading,
      diagnosticsLoading: state.diagnosticsLoading,
      summaryLoading: state.summaryLoading,
      error: state.error,
      autoRefresh: state.autoRefresh,
      fetchJobs: state.fetchJobs,
      fetchSummary: state.fetchSummary,
      fetchJobDiagnostics: state.fetchJobDiagnostics,
      setFilters: state.setFilters,
      resetFilters: state.resetFilters,
      setSelectedJobId: state.setSelectedJobId,
      setAutoRefresh: state.setAutoRefresh,
      nextPage: state.nextPage,
      prevPage: state.prevPage,
      retryJob: state.retryJob,
      cancelJob: state.cancelJob,
      updateJobPriority: state.updateJobPriority,
    })),
  );

  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.is_admin === true;

  // ── Sync filters FROM URL on mount ──────────────────────────────────────
  const didSyncFromUrl = useRef(false);
  useEffect(() => {
    if (didSyncFromUrl.current) return;
    didSyncFromUrl.current = true;

    const urlStatuses = searchParams.getAll("status") as QueueJobStatus[];
    const urlType = (searchParams.get("type") as QueueJobType | null) ?? null;
    const urlPriorityRaw = searchParams.get("priority");
    const urlPriority = urlPriorityRaw === null ? null : Number.parseInt(urlPriorityRaw, 10);
    const urlQ = searchParams.get("q") ?? "";
    const urlFrom = searchParams.get("from") ?? "";
    const urlTo = searchParams.get("to") ?? "";

    if (urlStatuses.length || urlType || Number.isFinite(urlPriority) || urlQ || urlFrom || urlTo) {
      setFilters({
        statuses: urlStatuses,
        jobType: urlType,
        priority: Number.isFinite(urlPriority) ? urlPriority : null,
        q: urlQ,
        from: urlFrom,
        to: urlTo,
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sync filters TO URL whenever they change ─────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams();
    for (const s of filters.statuses) params.append("status", s);
    if (filters.jobType) params.set("type", filters.jobType);
    if (filters.priority !== null) params.set("priority", String(filters.priority));
    if (filters.q) params.set("q", filters.q);
    if (filters.from) params.set("from", filters.from);
    if (filters.to) params.set("to", filters.to);
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  // ── Lista: no mount e a cada mudança de filtro ou página ─────────────────
  // Um efeito só. O "initial fetch" separado que existia aqui disparava a
  // mesma busca que este efeito já faz no mount — eram dois `GET /api/jobs`
  // em toda abertura da fila.
  useEffect(() => {
    void fetchJobs();
  }, [filters, pagination, fetchJobs]);

  // ── KPIs: não dependem de filtro nem de página ───────────────────────────
  useEffect(() => {
    void fetchSummary();
  }, [fetchSummary]);

  // ── Poll de 5 s, com guarda de in-flight, pausa em aba escondida,
  //    cancelamento no unmount e recuo quando o servidor demora ─────────────
  const pollTick = useCallback(
    async (signal: AbortSignal) => {
      await Promise.all([
        fetchJobs({ signal, silent: true }),
        fetchSummary({ signal, silent: true }),
      ]);
    },
    [fetchJobs, fetchSummary],
  );
  usePolling(pollTick, { intervalMs: POLL_INTERVAL_MS, enabled: autoRefresh });

  const handleRefreshNow = useCallback(() => {
    void fetchJobs();
    void fetchSummary();
  }, [fetchJobs, fetchSummary]);

  // Estável: é prop de uma tabela cujas linhas são memoizadas.
  const handleSelectJob = useCallback(
    (id: string) => setSelectedJobId(selectedJobId === id ? null : id),
    [selectedJobId, setSelectedJobId],
  );

  const selectedJob = jobs.find((j) => j.id === selectedJobId) ?? null;

  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden">
      {/* Top bar */}
      <div className="flex shrink-0 items-center justify-between border-b border-ink-800 px-6 py-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-ink-100">Fila de Renders</h1>
          <p className="mt-0.5 text-xs text-ink-400">
            Acompanhamento operacional de jobs de processamento.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleRefreshNow}
            disabled={loading}
            className="rounded border border-ink-700 bg-ink-800 px-3 py-1.5 text-xs text-ink-300 hover:border-ink-500 hover:text-ink-100 disabled:opacity-50"
          >
            ↺ Atualizar agora
          </button>
          <button
            type="button"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`rounded border px-3 py-1.5 text-xs transition-colors ${
              autoRefresh
                ? "border-emerald-800 bg-emerald-900/30 text-emerald-300 hover:bg-emerald-900/60"
                : "border-ink-700 bg-ink-800 text-ink-500 hover:border-ink-500 hover:text-ink-200"
            }`}
            aria-pressed={autoRefresh}
          >
            {autoRefresh ? "Auto ativo" : "Auto pausado"}
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* Main area */}
        <div className="flex min-w-0 flex-1 flex-col gap-4 overflow-hidden px-6 py-4">
          {/* KPIs */}
          <QueueKpis summary={summary} loading={summaryLoading} />

          {/* Filters */}
          <div className="shrink-0">
            <QueueFilters
              filters={filters}
              onFiltersChange={setFilters}
              onReset={resetFilters}
            />
          </div>

          {/* Error banner */}
          {error && (
            <p className="shrink-0 rounded border border-red-800 bg-red-950/40 px-3 py-2 text-xs text-red-300">
              {error}
            </p>
          )}

          {/* Table */}
          <QueueTable
            jobs={jobs}
            total={total}
            offset={pagination.offset}
            limit={pagination.limit}
            selectedJobId={selectedJobId}
            loading={loading}
            isAdmin={isAdmin}
            onSelectJob={handleSelectJob}
            onUpdatePriority={updateJobPriority}
            onNextPage={nextPage}
            onPrevPage={prevPage}
          />
        </div>

        {/* Drawer */}
        {selectedJob && (
          <QueueJobDrawer
            job={selectedJob}
            diagnostics={diagnosticsByJobId[selectedJob.id] ?? null}
            diagnosticsLoading={diagnosticsLoading}
            isAdmin={isAdmin}
            onClose={() => setSelectedJobId(null)}
            onRetry={retryJob}
            onCancel={cancelJob}
            onFetchDiagnostics={fetchJobDiagnostics}
          />
        )}
      </div>
    </section>
  );
}
